import type { ApolloQueryResult } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { MessageSorts, Sort } from '@aurora/shared-generated/types/graphql-schema-types';
import { SortDirection } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { useUID, useUIDSeed } from 'react-uid';
import useTranslation from '../../useTranslation';
import localStyles from './MessageListMenu.module.pcss';

const MessageListMenuBody = dynamic(() => import('./MessageListMenuBody'), {
  ssr: false
});

export interface MessageListMenuProps<TData> {
  /**
   * The list of sort and filter items to display in the menu.
   */
  items: MessageListMenuItem[];
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Callback function when an item in the menu is selected.
   *
   * @callback
   * @param item - the selected `MessageListMenuItemDefinition`
   */
  onChange?: (item: MessageListMenuItemDefinition) => Promise<void | ApolloQueryResult<TData>>;
  /**
   * The default selected item. This will default to the first item passed in the
   * `items` parameter if not explicitly specified.
   */
  defaultItem: MessageListMenuItem;
  /**
   * Set a custom element for this component.
   */
  as?: React.ElementType;
  /**
   * The name of the query parameter that is updated when an item is selected from the menu.
   */
  queryParameterName?:
    | EndUserQueryParams.MESSAGE_LIST_MENU_QUERY_PARAMETER
    | EndUserQueryParams.SORT_MENU_QUERY_PARAMETER;
  /**
   * Align the menu to the right side of the Dropdown toggle.
   */
  alignRight?: boolean;

  /**
   * Render the message body on mount.
   */
  renderOnMount?: boolean;
}

export enum MessageListMenuItem {
  POST_TIME_ASC = 'postTimeAsc',
  POST_TIME_DESC = 'postTimeDesc',
  KUDOS_SUM_WEIGHT_ASC = 'kudosSumWeightAsc',
  KUDOS_SUM_WEIGHT_DESC = 'kudosSumWeightDesc'
}

export interface MessageListMenuItemDefinition {
  sorts: MessageSorts;
}

const directionAsc: Sort = {
  direction: SortDirection.Asc
};

const directionDesc: Sort = {
  direction: SortDirection.Desc
};

export const messageListMenuItemMap: Record<MessageListMenuItem, MessageListMenuItemDefinition> = {
  [MessageListMenuItem.POST_TIME_ASC]: {
    sorts: {
      postTime: directionAsc
    }
  },
  [MessageListMenuItem.POST_TIME_DESC]: {
    sorts: {
      postTime: directionDesc
    }
  },
  [MessageListMenuItem.KUDOS_SUM_WEIGHT_ASC]: {
    sorts: {
      kudosSumWeight: directionAsc
    }
  },
  [MessageListMenuItem.KUDOS_SUM_WEIGHT_DESC]: {
    sorts: {
      kudosSumWeight: {
        direction: SortDirection.Desc,
        order: 0
      },
      postTime: {
        direction: SortDirection.Asc,
        order: 1
      }
    }
  }
};

/**
 * Displays a configurable list of sorts and filters that can be applied to a list of messages.
 * Currently only supports sorting by post time and kudos count; other sorts and filters will
 * be added as needed.
 *
 * @constructor
 *
 * @author Adam Ayres
 */
const MessageListMenu = <TData,>({
  items,
  as,
  className,
  onChange = (): Promise<void> => null,
  defaultItem,
  queryParameterName = EndUserQueryParams.MESSAGE_LIST_MENU_QUERY_PARAMETER,
  alignRight = false,
  renderOnMount = false
}: MessageListMenuProps<TData>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const {
    formatMessage,
    loading: textLoading,
    FormattedMessage
  } = useTranslation(EndUserComponent.MESSAGE_LIST_MENU);
  const uidSeed = useUIDSeed();
  const uid = useUID();

  const [selectedItem, setSelectedItem] = useState<MessageListMenuItem>(defaultItem);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  if (textLoading) {
    return null;
  }

  /**
   * This function handles the toggle event of the dropdown
   */
  const onToggle = (
    isOpen: boolean,
    event: React.SyntheticEvent<Dropdown>,
    metadata: {
      source: 'select' | 'click' | 'rootClose' | 'keydown';
    }
  ) => {
    if (metadata.source === 'click') {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  return (
    <Dropdown
      as={as}
      className={cx(className)}
      show={showDropdown}
      onToggle={onToggle}
      onKeyDown={event => {
        if (event.key === 'Tab') {
          setShowDropdown(false);
        }
      }}
    >
      <Dropdown.Toggle
        as={Button}
        variant={ButtonVariant.LINK}
        id={uidSeed('message-list-menu')}
        className={cx('lia-g-dropdown-toggle')}
        data-testid="MessageListToggle"
        role="combobox"
        aria-autocomplete="list"
        aria-activedescendant={`lia-menu-${selectedItem}`}
        aria-controls={`listbox-${uid}`}
        aria-live="assertive"
        aria-atomic="true"
      >
        <FormattedMessage
          id={'sortedBy.item'}
          values={{
            itemName: selectedItem,
            style: function renderChunks(chunks): React.ReactNode {
              return <span className={cx('sr-only')}>{chunks}</span>;
            }
          }}
        />
      </Dropdown.Toggle>
      <Dropdown.Menu
        id={`listbox-${uid}`}
        className={cx({ 'lia-g-popper-right': alignRight })}
        popperConfig={dropdownPopperConfig}
        align={alignRight ? 'right' : 'left'}
        renderOnMount={renderOnMount}
        data-testid="MessageListMenu"
      >
        <Dropdown.Header id={`menuTitle-${uid}`}>{formatMessage('sortTitle')}</Dropdown.Header>
        <ul className={cx('lia-sort-list')} role="listbox" aria-labelledby={`menuTitle-${uid}`}>
          <MessageListMenuBody
            items={items}
            className={className}
            defaultItem={defaultItem}
            onChange={onChange}
            alignRight={alignRight}
            as={as}
            queryParameterName={queryParameterName}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            cx={cx}
          />
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MessageListMenu;
